/** Bandeja de aprobaciones móvil (mismo contrato workflow que la web, shape real del backend). */
import { mobileApiClient, newIdempotencyKey } from './mobileApiClient';

export type WorkflowTaskStatus =
  | 'open'
  | 'unavailable'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'reassigned';

export type WorkflowAssigneeSnapshot = {
  display_name: string;
  membership_id?: string;
  user_id?: string;
};

/** Lectura real de GET /api/v1/workflow-tasks/mine (WorkflowTaskRead, sin título ni entidad). */
export type WorkflowTask = {
  id: string;
  tenant_id: string;
  instance_id: string;
  instance_step_id: string;
  assigned_user_id: string;
  assigned_at: string;
  due_at: string | null;
  status: WorkflowTaskStatus;
  revision: number;
  assignee_snapshot: WorkflowAssigneeSnapshot;
};

export type WorkflowTaskInstanceSummary = {
  id: string;
  entity_code: string;
  entity_id: string;
  workflow_point_code: string;
  status?: string;
};

export type WorkflowTaskDocumentSummary = {
  label: string;
  entity_code: string;
  entity_id: string;
  business_status: string;
  document_revision: number;
};

export type WorkflowTaskAvailableAction = {
  action_code: string;
  label: string;
  workflow_point_code?: string | null;
  task_id?: string | null;
  requires_reason?: boolean;
  enabled?: boolean;
};

/** Lectura real de GET /api/v1/workflow-tasks/{id} (WorkflowTaskDetailRead). */
export type WorkflowTaskDetail = {
  task: WorkflowTask;
  task_revision: number;
  instance_summary: WorkflowTaskInstanceSummary;
  document_summary: WorkflowTaskDocumentSummary;
  snapshot: unknown;
  available_actions: WorkflowTaskAvailableAction[];
};

/** Acción disponible del contexto de flujo (shape real de WorkflowAvailableActionRead).
 *
 * El servidor entrega `action_code` + `requires_reason` + `enabled` +
 * `disabled_code`/`disabled_reason`. Se acepta `code` como alias para
 * compatibilidad con respuestas ya normalizadas.
 */
export type WorkflowContextAction = {
  code: string;
  action_code?: string;
  label: string;
  requires_reason: boolean;
  enabled?: boolean;
  disabled?: boolean;
  disabled_code?: string | null;
  disabled_reason?: string | null;
  workflow_point_code?: string | null;
  task_id?: string | null;
};

export function normalizeContextAction(raw: Record<string, unknown>): WorkflowContextAction {
  const code =
    typeof raw['action_code'] === 'string'
      ? (raw['action_code'] as string)
      : typeof raw['code'] === 'string'
        ? (raw['code'] as string)
        : '';
  const enabled = typeof raw['enabled'] === 'boolean' ? (raw['enabled'] as boolean) : undefined;
  const disabled =
    typeof raw['disabled'] === 'boolean'
      ? (raw['disabled'] as boolean)
      : enabled === undefined
        ? undefined
        : !enabled;
  return {
    code,
    action_code: typeof raw['action_code'] === 'string' ? (raw['action_code'] as string) : undefined,
    label: typeof raw['label'] === 'string' ? (raw['label'] as string) : code,
    requires_reason: raw['requires_reason'] === true,
    enabled,
    disabled,
    disabled_code: (raw['disabled_code'] as string | null) ?? null,
    disabled_reason: (raw['disabled_reason'] as string | null) ?? null,
    workflow_point_code: (raw['workflow_point_code'] as string | null) ?? null,
    task_id:
      raw['task_id'] === null || raw['task_id'] === undefined ? null : String(raw['task_id']),
  };
}

/** Punto del recorrido del documento (shape real de WorkflowPointRead). */
export type WorkflowContextPoint = {
  code: string;
  label: string;
};

function normalizeContextPoint(raw: Record<string, unknown>): WorkflowContextPoint {
  const code =
    typeof raw['workflow_point_code'] === 'string'
      ? (raw['workflow_point_code'] as string)
      : typeof raw['code'] === 'string'
        ? (raw['code'] as string)
        : '';
  return {
    code,
    label: typeof raw['label'] === 'string' ? (raw['label'] as string) : code,
  };
}

export type WorkflowContext = {
  actions: WorkflowContextAction[];
  points: WorkflowContextPoint[];
  document_revision: number;
  business_status?: string;
};

function normalizeContext(raw: Record<string, unknown>): WorkflowContext {
  const actions = Array.isArray(raw['actions'])
    ? (raw['actions'] as Record<string, unknown>[]).map(normalizeContextAction)
    : [];
  const points = Array.isArray(raw['points'])
    ? (raw['points'] as Record<string, unknown>[]).map(normalizeContextPoint)
    : [];
  const revision = raw['document_revision'];
  return {
    actions,
    points,
    document_revision: typeof revision === 'number' ? revision : Number(revision ?? 0) || 0,
    business_status: typeof raw['business_status'] === 'string' ? (raw['business_status'] as string) : undefined,
  };
}

export type DecideTaskInput = {
  decision: 'approve' | 'reject';
  reason?: string;
  comments?: string;
  expectedTaskRevision: number;
};

export const tasksService = {
  async mine(status?: 'open'): Promise<WorkflowTask[]> {
    const { data } = await mobileApiClient.get('/api/v1/workflow-tasks/mine', {
      params: status ? { status } : {},
    });
    return (data.items ?? data) as WorkflowTask[];
  },
  async get(taskId: string): Promise<WorkflowTaskDetail> {
    const { data } = await mobileApiClient.get(`/api/v1/workflow-tasks/${taskId}`);
    return data as WorkflowTaskDetail;
  },
  async context(entityCode: string, entityId: string): Promise<WorkflowContext> {
    const { data } = await mobileApiClient.get(`/api/v1/workflow-context/${entityCode}/${entityId}`);
    return normalizeContext(data as Record<string, unknown>);
  },
  async decide(taskId: string, input: DecideTaskInput): Promise<void> {
    await mobileApiClient.post(
      `/api/v1/workflow-tasks/${taskId}/decision`,
      {
        decision: input.decision,
        reason: input.reason,
        comments: input.comments,
        expected_task_revision: input.expectedTaskRevision,
      },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
  },
  async withdraw(instanceId: string, reason?: string): Promise<void> {
    await mobileApiClient.post(
      `/api/v1/workflow-instances/${instanceId}/withdraw`,
      { reason },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
  },
};
